import { create } from 'zustand'
import type {
  CallIncomingEvent,
  CallAnsweredEvent,
  CallEndedEvent,
} from '@kc3/contracts'
import type { CallSessionSnapshot } from '../socket/types.js'

export type CallUiStatus = 'ringing' | 'answered' | 'on_hold' | 'ended'

/**
 * Map a server-side `CallSessionSnapshot` status to the UI's
 * `CallUiStatus`. Server uses uppercase `RINGING|ANSWERED|ON_HOLD|ENDED`;
 * UI uses lowercase variants.
 */
function uiStatusFromSnapshot(
  status: CallSessionSnapshot['status'],
): CallUiStatus {
  switch (status) {
    case 'RINGING':
      return 'ringing'
    case 'ANSWERED':
      return 'answered'
    case 'ON_HOLD':
      return 'on_hold'
    case 'ENDED':
      return 'ended'
  }
}

export interface ActiveCallSnapshot {
  callId: string
  callerCLI: string
  /** Resolved client-portal visitor name, when the caller is a Call Support visitor with one on file. */
  callerDisplayName?: string
  /** Resolved client-portal visitor email, same conditions as callerDisplayName. */
  callerEmail?: string
  /** Resolved clients.id, same conditions as callerDisplayName. */
  clientId?: string
  status: CallUiStatus
  startedAt: string
  answeredAt?: string
}

interface CallStoreState {
  activeCall: ActiveCallSnapshot | null
  incomingCall: CallIncomingEvent | null
  /**
   * Cache a `call:incoming` event WITHOUT setting `activeCall`.
   *
   * `call:incoming` is broadcast to every agent in the tenant room
   * (ws-gateway/rabbitmq-consumer.ts), but only the ACD-selected agent
   * receives the SIP INVITE. Setting `activeCall` here made every agent's
   * header show "Client Portal Caller" even when their softphone never
   * rang — the exact symptom reported for client-portal Call Support.
   *
   * The caller info is cached so `activateFromIncoming` can populate
   * `activeCall` with the resolved display name / email / clientId when
   * the SIP INVITE actually arrives at THIS agent's softphone.
   */
  setIncoming: (event: CallIncomingEvent) => void
  /**
   * Activate `activeCall` from the cached `call:incoming` event when the
   * SIP INVITE arrives at this agent's softphone. `from` is the caller
   * identifier (visitor SIP user or E.164 number) extracted from the
   * JsSIP session's remote identity. Returns true if the cached event
   * matched and `activeCall` was set; false if no cache matched (caller
   * should set a minimal `activeCall` from the SIP URI alone).
   */
  activateFromIncoming: (from: string) => boolean
  setAnswered: (event: CallAnsweredEvent) => void
  setEnded: (event: CallEndedEvent) => void
  setHold: (held: boolean) => void
  setActiveCallFromSnapshot: (snapshot: CallSessionSnapshot) => void
  clearCall: () => void
}

export const useCallStore = create<CallStoreState>()((set, get) => ({
  activeCall: null,
  incomingCall: null,
  setIncoming: (event) => {
    console.log('[callStore] setIncoming (cache only, no activeCall):', event)
    set({ incomingCall: event })
  },
  activateFromIncoming: (from) => {
    const cached = get().incomingCall
    if (cached && cached.from === from) {
      set({
        activeCall: {
          callId: cached.callId,
          callerCLI: cached.from,
          ...(cached.callerDisplayName != null && { callerDisplayName: cached.callerDisplayName }),
          ...(cached.callerEmail != null && { callerEmail: cached.callerEmail }),
          ...(cached.clientId != null && { clientId: cached.clientId }),
          status: 'ringing',
          startedAt: cached.timestamp,
        },
      })
      return true
    }
    return false
  },
  setAnswered: (event) =>
    set((state) => {
      const base: ActiveCallSnapshot = state.activeCall
        ? { ...state.activeCall, status: 'answered', answeredAt: event.answeredAt }
        : state.incomingCall && state.incomingCall.callId === event.callId
          ? {
              callId: event.callId,
              callerCLI: state.incomingCall.from,
              ...(state.incomingCall.callerDisplayName != null && { callerDisplayName: state.incomingCall.callerDisplayName }),
              ...(state.incomingCall.callerEmail != null && { callerEmail: state.incomingCall.callerEmail }),
              ...(state.incomingCall.clientId != null && { clientId: state.incomingCall.clientId }),
              status: 'answered',
              startedAt: state.incomingCall.timestamp,
              answeredAt: event.answeredAt,
            }
          : {
              callId: event.callId,
              callerCLI: '',
              status: 'answered',
              startedAt: event.answeredAt,
              answeredAt: event.answeredAt,
            }
      return { incomingCall: null, activeCall: base }
    }),
  // Deliberately does NOT clear activeCall — CreateConversationPanel reads
  // status === 'ended', and useSoftphone calls clearCall() separately once
  // its own JsSIP session actually tears down (see
  // src/store/__tests__/callStore-clear.test.ts, which pins this contract).
  setEnded: (_event) =>
    set((state) =>
      state.activeCall
        ? { activeCall: { ...state.activeCall, status: 'ended' } }
        : {},
    ),
  setHold: (held) =>
    set((state) => {
      if (!state.activeCall) return {}
      const cur = state.activeCall.status
      if (held && cur === 'answered') {
        return { activeCall: { ...state.activeCall, status: 'on_hold' } }
      }
      if (!held && cur === 'on_hold') {
        return { activeCall: { ...state.activeCall, status: 'answered' } }
      }
      return {}
    }),
  setActiveCallFromSnapshot: (snapshot) =>
    set({
      incomingCall: null,
      activeCall: {
        callId: snapshot.callId,
        callerCLI: snapshot.callerCLI,
        ...(snapshot.callerDisplayName != null && { callerDisplayName: snapshot.callerDisplayName }),
        ...(snapshot.callerEmail != null && { callerEmail: snapshot.callerEmail }),
        ...(snapshot.clientId != null && { clientId: snapshot.clientId }),
        status: uiStatusFromSnapshot(snapshot.status),
        startedAt: snapshot.startedAt,
      },
    }),
  clearCall: () => set({ activeCall: null, incomingCall: null }),
}))
